import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { compact } from "lodash";
import { SingleSelectOption } from "@/components/controls/SingleSelect";
import QuestionContainer from "@/components/shared/QuestionContainer";
import { HelpTextTooltip } from "@/components/shared/HelpTextTooltip";
import { usePeople, usePeopleByCompany } from "@/entities/people/hooks";
import { getFullName } from "@/entities/people/helpers";
import { useContract } from "@/pages/contracts/hooks";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { OnboardingStageWorkflowItem } from "@/pages/settings/components/contractsSettings/types";
import { FieldControl } from "@/utils/fieldRendering/FieldControl";
import { ResponseType } from "@/utils/fieldRendering/shared";
import {
  WorkflowItemResponseDetails,
  WorkflowSuggestionAction
} from "@/pages/contracts/hooks";
import { OnboardingWorkflowResponseValue } from "../types";
import { responseValueText } from "../responseText";
import { OnboardingItemDreamerIndication } from "./OnboardingItemDreamerIndication";

interface Props {
  contractId: string;
  item: OnboardingStageWorkflowItem;
  value: OnboardingWorkflowResponseValue;
  onChange: (value: OnboardingWorkflowResponseValue) => void;
  /** Dreamer provenance for this item; omitted when contract autofill is off. */
  details?: WorkflowItemResponseDetails;
  onSuggestionResolved?: (action: WorkflowSuggestionAction) => void;
}

// Settings stores a FieldDisplayType in responseType.type; older rows a string.
const getFieldType = (item: OnboardingStageWorkflowItem): string => {
  const { responseType } = item;
  if (typeof responseType === "string") return responseType;
  if (responseType && typeof responseType.type === "string")
    return responseType.type;
  return FieldDisplayType.TEXT;
};

const getConfig = (item: OnboardingStageWorkflowItem): ResponseType =>
  item.responseType && typeof item.responseType === "object"
    ? (item.responseType as ResponseType)
    : {};

// Workflow select options are stored as [{ id, option }] by the settings form.
const getSelectOptions = (config: ResponseType): SingleSelectOption[] => {
  if (!Array.isArray(config.options)) return [];
  return config.options.flatMap((option) => {
    if (typeof option === "string")
      return [{ id: option, displayName: option }];
    if (option && typeof option === "object") {
      const { id, option: label } = option as {
        id?: unknown;
        option?: unknown;
      };
      if (typeof id === "string")
        return [{ id, displayName: String(label ?? id) }];
    }
    return [];
  });
};

const usePeopleInCompanyOptions = (
  contractId: string,
  enabled: boolean
): SingleSelectOption[] => {
  const { data: contract } = useContract(enabled ? contractId : "");
  const { data: companyPeopleIds } = usePeopleByCompany(
    contract?.companyId ?? ""
  );
  const { entities: companyPeople } = usePeople(companyPeopleIds?.ids ?? []);

  return useMemo(
    () =>
      compact(companyPeople)
        .map((person) => ({
          id: person.id,
          displayName: getFullName(person),
          subtitle: person.jobTitle || undefined
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [companyPeople]
  );
};

const OnboardingWorkflowItem: React.FC<Props> = ({
  contractId,
  item,
  value,
  onChange,
  details,
  onSuggestionResolved
}) => {
  const { t } = useTranslation();
  const fieldType = getFieldType(item);
  const config = getConfig(item);
  const peopleOptions = usePeopleInCompanyOptions(
    contractId,
    fieldType === FieldDisplayType.PEOPLE_IN_COMPANY
  );

  const options =
    fieldType === FieldDisplayType.PEOPLE_IN_COMPANY
      ? peopleOptions
      : getSelectOptions(config);

  const control = (
    <FieldControl
      fieldType={fieldType}
      config={config}
      value={value}
      onChange={onChange}
      name={`onboarding-workflow-item-${item.id}`}
      options={options}
      checkboxLabel={item.name}
    />
  );

  // Plain text, as the deal popovers show values, so screen readers can compare them.
  const renderValueText = (shown: OnboardingWorkflowResponseValue) =>
    responseValueText(fieldType, shown, options, t);

  const indicator = details ? (
    <OnboardingItemDreamerIndication
      contractId={contractId}
      itemId={item.id}
      value={value}
      details={details}
      renderValue={renderValueText}
      onResolved={onSuggestionResolved}
    />
  ) : null;

  // A checklist item is its own label, so it skips the question container.
  if (fieldType === FieldDisplayType.CHECKLIST_ITEM) {
    return (
      <div className="flex items-center gap-2">
        {control}
        <HelpTextTooltip tooltipText={item.tooltipText} />
        {indicator}
      </div>
    );
  }

  return (
    <QuestionContainer
      label={item.name}
      isMandatory={item.isGuardrails}
      tooltipText={item.tooltipText}
      indicator={indicator}
    >
      {control}
    </QuestionContainer>
  );
};

export default OnboardingWorkflowItem;
